/**
 * @render react
 * @name CalendarReminder
 */

import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { withPolaris } from 'Components/protons/Polaris';
import { Icon } from 'Components/atoms/Icon';
import { Link } from 'Components/molecules/Link';
import { Tooltip } from 'Components/molecules/Tooltip';

import { createEvent } from 'ics';

import './_style.scss';

/**
 * CalendarReminder component
 * @param {Object} props - The properties object.
 * @param {Object} props.context - The context object containing additional information.
 * @param {string} props.context.cssPrefix - The CSS prefix for styling.
 * @param {number} props.daysToRemindIn - The number of days to remind the user in.
 * @param {string} props.eventDescription - The description of the event.
 * @param {string} props.eventTitle - The title of the event.
 * @param {string} props.text - The text to display for the calendar reminder.
 * @param {string} props.tooltipIcon - The icon for the tooltip.
 * @param {string} props.tooltipInfo - The information to be displayed in the tooltip.
 * @param {string} props.tooltipCloseIcon - The icon for closing
 * @param {Function} props.onClick - The function to call when a user clicks the calendar reminder link.
 * @param {string} props.filename - The filename for the calendar reminder.
 * @returns {React.ReactElement} - The rendered MOTChecker component.
 */
const CalendarReminder = ({
  context: { cssPrefix },
  dateToRemind,
  eventDescription,
  eventTitle,
  text,
  tooltipIcon,
  tooltipInfo,
  tooltipCloseIcon,
  onClick,
  filename,
}) => {
  const [icsContent, setIcsContent] = useState('');
  const [icsError, setIcsError] = useState('');
  const icsEvent = useMemo(() => ({
    start: [
      dateToRemind.getFullYear(),
      dateToRemind.getMonth() + 1,
      dateToRemind.getDate(),
      dateToRemind.getHours(),
      dateToRemind.getMinutes(),
    ],
    duration: { days: 1 },
    title: eventTitle,
    description: eventDescription,
    status: 'CONFIRMED',
  }), [dateToRemind, eventDescription, eventTitle]);

  useEffect(() => {
    createEvent(icsEvent, (error, value) => {
      if (error) {
        setIcsError(error);
        return;
      }

      setIcsContent(`data:text/calendar;charset=utf-8,${encodeURIComponent(value)}`);
    });
  }, [icsEvent]);

  return !icsError && (
    <div className={`${cssPrefix}__calendar-reminder`}>
      <Link
        href={icsContent}
        download={filename}
        extraClassNames={{ [`${cssPrefix}__calendar-reminder--link`]: true }}
        onClick={onClick}
      >
        <Icon icon={`${cssPrefix}__ico-calendar`} />
        {text}
      </Link>
      <Tooltip
        icon={tooltipIcon}
        content={tooltipInfo}
        closeLabel={<Icon icon={tooltipCloseIcon} />}
        extraClassNames={{ [`${cssPrefix}__calendar-reminder--tooltip`]: true }}
      />
    </div>
  );
};

/**
 * Valid props
 */
CalendarReminder.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  dateToRemind: PropTypes.instanceOf(Date).isRequired,
  eventDescription: PropTypes.string,
  eventTitle: PropTypes.string.isRequired,
  text: PropTypes.string,
  tooltipIcon: PropTypes.string,
  tooltipInfo: PropTypes.string,
  tooltipCloseIcon: PropTypes.string,
  onClick: PropTypes.func,
  filename: PropTypes.string,
};

/**
 * Default props
 */
CalendarReminder.defaultProps = {
  context: {
    cssPrefix: 'polaris',
  },
  eventDescription: '',
  text: 'Remind me!',
  tooltipIcon: 'polaris__ico-info',
  tooltipInfo: 'Download a calendar reminder.',
  tooltipCloseIcon: 'polaris__ico-close',
  onClick: () => {},
  filename: 'calendarReminder.ics',
};

export default withPolaris(CalendarReminder);
